import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';
import { authRateLimiter } from '@/utils/rateLimiting';
import { AnalyticsTracker } from '@/utils/monitoring';
import { defaultSignedInPath } from '@/config/esportsDomain';

interface AuthContextProps {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ data?: any; error?: any }>;
  signUp: (email: string, password: string, metadata?: { first_name?: string; last_name?: string }) => Promise<{ data?: any; error?: any }>;
  signOut: () => Promise<{ error?: any }>;
  supabase: typeof supabase;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Use ref to track current user without causing re-renders or stale closures
  const currentUserRef = React.useRef<User | null>(null);

  // Keep ref in sync with user state
  React.useEffect(() => {
    currentUserRef.current = user;
  }, [user]);

  useEffect(() => {
    logger.debug('Setting up auth state listener...');
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.debug('Auth state changed:', event, session?.user?.id);
        
        // Handle password recovery - redirect to reset password page
        if (event === 'PASSWORD_RECOVERY') {
          logger.debug('Password recovery detected, redirecting to reset password');
          if (session) {
            setSession(session);
            setUser(session.user);
            currentUserRef.current = session.user;
          }
          setLoading(false);
          // Use window.location to ensure full page navigation with session intact
          window.location.href = '/reset-password';
          return;
        }
        
        if (event === 'SIGNED_OUT' || !session) {
          logger.debug('User signed out or no session');
          setSession(null);
          setUser(null);
          currentUserRef.current = null;
          setLoading(false);
          return;
        }
        
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          const currentUserId = currentUserRef.current?.id;
          const newUserId = session.user.id;

          // Only treat as a security incident on an explicit SIGNED_IN where the
          // current in-memory user is different from the new session user.
          // TOKEN_REFRESHED and cross-tab storage sync should never log the user out.
          if (event === 'SIGNED_IN' && currentUserId && currentUserId !== newUserId) {
            logger.error('SECURITY: Session user mismatch detected!', {
              currentUserId,
              newUserId,
            });

            setSession(null);
            setUser(null);
            currentUserRef.current = null;
            cleanupAuthState();
            await supabase.auth.signOut({ scope: 'global' });

            toast({
              title: "Security Alert",
              description: "Session mismatch detected. Please log in again.",
              variant: "destructive",
            });

            setTimeout(() => {
              window.location.href = '/login';
            }, 100);
            return;
          }

          logger.debug('User signed in or token refreshed:', newUserId);
          setSession(session);
          setUser(session.user);
          currentUserRef.current = session.user;

          setTimeout(() => {
            logger.debug('Auth state updated successfully');
          }, 0);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    const initializeAuth = async () => {
      try {
        logger.debug('Checking for existing session...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          logger.error('Error getting session:', error);
          setLoading(false);
          return;
        }
        
        if (session) {
          logger.debug('Found existing session');
          setSession(session);
          setUser(session.user);
        }
      } catch (error) {
        logger.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      logger.debug('Cleaning up auth listener...');
      subscription.unsubscribe();
    };
  }, []);

  // Helper function to clean up auth state
  const cleanupAuthState = () => {
    // Clear all Supabase auth keys from localStorage
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear from sessionStorage if in use
    if (typeof sessionStorage !== 'undefined') {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
          sessionStorage.removeItem(key);
        }
      });
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      // CRITICAL: Clear state first, then sign out to prevent race conditions
      setSession(null);
      setUser(null);
      
      // Force complete sign out with proper cleanup order
      try {
        await supabase.auth.signOut({ scope: 'global' });
        // Wait for signOut to complete before cleaning storage
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (err) {
        logger.error('Error during pre-login cleanup:', err);
      }
      
      // Clean up ALL storage after signOut completes
      cleanupAuthState();

      // Check rate limiting
      const rateLimitCheck = authRateLimiter.canAttempt();
      if (!rateLimitCheck.allowed) {
        const minutes = Math.ceil((rateLimitCheck.remainingTime || 0) / 60);
        const errorMessage = `Too many failed attempts. Please try again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`;
        toast({
          title: "Login temporarily blocked",
          description: errorMessage,
          variant: "destructive",
        });
        return { error: { message: errorMessage } };
      }

      logger.debug('Attempting sign in for:', email.trim());
      
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email: email.trim(), 
        password 
      });
      
      if (error) {
        logger.error('Sign in error:', error);
        authRateLimiter.recordFailedAttempt();
        
        // Generic error message to prevent user enumeration
        const genericMessage = "Invalid email or password. Please check your credentials and try again.";
        toast({
          title: "Login failed",
          description: genericMessage,
          variant: "destructive",
        });
        return { data, error: { message: genericMessage } };
      } else if (data.user) {
        logger.debug('Sign in successful for user:', data.user.id);
        authRateLimiter.recordSuccessfulAttempt();
        
        // Validate that the session matches the expected user
        const { data: { session: verifySession } } = await supabase.auth.getSession();
        if (!verifySession || verifySession.user.id !== data.user.id) {
          logger.error('SECURITY: Session verification failed after login');
          await supabase.auth.signOut({ scope: 'global' });
          cleanupAuthState();
          
          toast({
            title: "Login failed",
            description: "Session verification failed. Please try again.",
            variant: "destructive",
          });
          return { error: { message: "Session verification failed" } };
        }
        
        // Identify user in PostHog
        AnalyticsTracker.identifyUser(data.user.id, {
          email: data.user.email,
        });
        
        toast({
          title: "Login successful",
          description: "Welcome back!",
        });
        
        // Force page reload to ensure clean state
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 100);
      }
      
      return { data, error };
    } catch (error: any) {
      logger.error('Unexpected sign in error:', error);
      authRateLimiter.recordFailedAttempt();
      toast({
        title: "Login failed",
        description: "An unexpected error occurred during login",
        variant: "destructive",
      });
      return { error: { message: "An unexpected error occurred during login" } };
    }
  };

  const signUp = async (email: string, password: string, metadata?: { first_name?: string; last_name?: string }) => {
    try {
      // CRITICAL: Clear state first
      setSession(null);
      setUser(null);
      
      // Force complete sign out before signup
      try {
        await supabase.auth.signOut({ scope: 'global' });
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (err) {
        logger.error('Error during pre-signup cleanup:', err);
      }
      
      // Clean up storage after signOut completes
      cleanupAuthState();

      logger.debug('Attempting sign up for:', email.trim());

      // The confirmation link returns an esports organizer to the esports
      // dashboard; a sports organizer goes on to choose a plan.
      const home = defaultSignedInPath(window.location.hostname);
      const { data, error } = await supabase.auth.signUp({ 
        email: email.trim(), 
        password,
        options: {
          emailRedirectTo: `${window.location.origin}${home === '/dashboard' ? '/pricing' : home}`,
          data: metadata || {}
        }
      });
      
      if (error) {
        logger.error('Sign up error:', error);
        toast({
          title: "Signup failed",
          description: error.message,
          variant: "destructive",
        });
      } else {
        logger.debug('Sign up successful for user:', data.user?.id);
        toast({
          title: "Signup successful",
          description: "Your account has been created successfully!",
        });
      }
      
      return { data, error };
    } catch (error: any) {
      logger.error('Unexpected sign up error:', error);
      toast({
        title: "Signup failed",
        description: "An unexpected error occurred during signup",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      const currentUserId = user?.id;
      logger.debug('Attempting sign out for user:', currentUserId);
      
      // Reset PostHog user identification
      AnalyticsTracker.resetUser();
      
      // CRITICAL: Clear state FIRST to prevent race conditions
      setSession(null);
      setUser(null);
      
      // Then perform sign out
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      
      // Wait for signOut to complete before cleaning storage
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Clean up ALL auth state after signOut completes
      cleanupAuthState();
      
      if (error) {
        logger.error('Sign out error:', error);
        toast({
          title: "Logout failed",
          description: error.message,
          variant: "destructive",
        });
      } else {
        logger.debug('Sign out successful for user:', currentUserId);
        toast({
          title: "Logged out",
          description: "You have been successfully logged out",
        });
      }
      
      // Force page reload for complete cleanup
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
      
      return { error };
    } catch (error: any) {
      logger.error('Unexpected sign out error:', error);
      toast({
        title: "Logout failed",
        description: error.message || "An unexpected error occurred during logout",
        variant: "destructive",
      });
      return { error };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut, supabase }}>
      {children}
    </AuthContext.Provider>
  );
};
